<script lang="ts">
  import { filtersStore } from "$lib/stores/filters.svelte";
  import { CornerUpRight } from "lucide-svelte";
  import Button from "$lib/components/ui/button/button.svelte";
  import { Badge } from "$lib/components/ui/badge";
  import { stateStore } from "$lib/stores/state.svelte";
  import X from "@lucide/svelte/icons/x";

  let { isFull = true } = $props();

  const hoverEffect =
    "hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground";

  function handleClick(id: string) {
    filtersStore.toggleCategory(id);
  }

  function handleClose(e: Event) {
    e.stopPropagation();

    stateStore.activePage = null;
  }

  function dragScroll(node: HTMLElement) {
    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    function onMouseDown(e: MouseEvent) {
      // Only activate if it's not a touch device
      if ("ontouchstart" in window) return;

      isDown = true;
      node.classList.add("dragging");
      startX = e.pageX - node.offsetLeft;
      scrollLeft = node.scrollLeft;
    }

    function onMouseLeave() {
      isDown = false;
      node.classList.remove("dragging");
    }

    function onMouseUp() {
      isDown = false;
      node.classList.remove("dragging");
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - node.offsetLeft;
      const walk = (x - startX) * 1; // multiplier = scroll speed
      node.scrollLeft = scrollLeft - walk;
    }

    node.addEventListener("mousedown", onMouseDown);
    node.addEventListener("mouseleave", onMouseLeave);
    node.addEventListener("mouseup", onMouseUp);
    node.addEventListener("mousemove", onMouseMove);

    return {
      destroy() {
        node.removeEventListener("mousedown", onMouseDown);
        node.removeEventListener("mouseleave", onMouseLeave);
        node.removeEventListener("mouseup", onMouseUp);
        node.removeEventListener("mousemove", onMouseMove);
      },
    };
  }
</script>

{#if isFull}
  <div class="p-4 mx-2 border-b border-black">
    <h3 class="text-xl text-foreground justify-between font-extrabold">
      Mit szeretnél csinálni?
    </h3>
    <p>Válaszd ki, mihez van kedved és megmutatjuk a térképen!</p>
  </div>
  <div class="p-2 space-y-2">
    {#each filtersStore.categories as category (category.id)}
      <Button
        onclick={() => handleClick(category.id)}
        variant={category.active ? "default" : "outline"}
        class="relative w-full flex-col border-foreground/50 hover:border-foreground h-full px-4 py-4 text-sm font-extrabold
    cursor-pointer whitespace-nowrap backdrop-blur-sm duration-300
    {hoverEffect}  bg-card hover:bg-card/50 {category.active
          ? 'bg-primary hover:bg-primary -translate-y-0.5 shadow-[2px_2px_0px_0px] shadow-foreground transition-none'
          : 'transition-all'}"
      >
        <X
          class="absolute right-2 top-2 !w-6 !h-6 {category.active
            ? 'visible'
            : 'hidden'}"
        />
        <div class="flex items-center w-full gap-4">
          <img
            src={`/assets/icons/${category.icon}.svg`}
            class="h-12"
            alt={category.label}
          />
          <div class="text-start text-lg">
            {category.label}
            <p class="font-medium text-sm font-sans">{category.description}</p>
          </div>
        </div>
        {#if category.active}
          <p
            class="w-full max-w-full font-medium overflow-hidden break-words pt-4 whitespace-normal text-start"
          >
            {category.detail}
          </p>
          <Button class="font-extrabold mr-auto" onclick={handleClose}>
            <CornerUpRight strokeWidth={5} />Irány a térkép
          </Button>
        {/if}
      </Button>
    {/each}
  </div>
{:else}
  <div
    use:dragScroll
    class="flex gap-1 overflow-x-auto pb-2 pt-1 scrollbar-thin select-none"
  >
    {#each filtersStore.categories as category (category.id)}
      <Badge
        variant={category.active ? "default" : "outline"}
        class="flex border-[1px] border-black items-center gap-2 px-2 py-2 text-sm font-extrabold
            cursor-pointer whitespace-nowrap backdrop-blur-sm
            {hoverEffect} transition-all duration-300
            {category.active
          ? 'bg-primary text-primary-foreground shadow-[2px_2px_0px_0px] shadow-foreground'
          : 'bg-card/90 hover:bg-accent/20'}"
        onclick={() => handleClick(category.id)}
      >
        <img
          src={`/assets/icons/${category.icon}.svg`}
          class="h-6"
          alt={category.label}
        />
        {category.label}
      </Badge>
    {/each}
  </div>
{/if}

<style>
  .scrollbar-thin::-webkit-scrollbar {
    height: 4px;
  }

  .scrollbar-thin::-webkit-scrollbar-track {
    background: oklch(var(--muted) / 0.5);
    border-radius: 2px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: oklch(var(--muted-foreground) / 0.3);
    border-radius: 2px;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: oklch(var(--muted-foreground) / 0.5);
  }
</style>
